
import React from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';
import { useCreateConversation } from '@/hooks/useConversations';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface MessageBreederButtonProps {
  listingId: string;
  breederId: string;
  listingTitle: string;
  listingUrl: string;
  className?: string;
}

export const MessageBreederButton = ({ 
  listingId, 
  breederId, 
  listingTitle, 
  listingUrl,
  className 
}: MessageBreederButtonProps) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const createConversationMutation = useCreateConversation();

  const handleClick = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Don't allow messaging yourself
    if (user.id === breederId) {
      return;
    }

    try {
      // Determine the correct listing ID and breeder ID based on context
      let conversationListingId: string;
      let actualBreederId: string;
      
      if (listingUrl.includes('/buyer/')) {
        // This is a buyer profile - user is messaging a buyer, so current user should be breeder
        // Use the buyer's ID as listing ID (for profile conversations)
        conversationListingId = breederId;
        actualBreederId = user.id; // Current user is the breeder
      } else if (listingId.startsWith('profile-') || listingUrl.includes('/breeder/')) {
        // This is a breeder profile - use the breeder's ID as listing ID
        conversationListingId = breederId;
        actualBreederId = breederId;
      } else {
        // This is a regular listing
        conversationListingId = listingId;
        actualBreederId = breederId;
      }
      
      const conversation = await createConversationMutation.mutateAsync({
        listing_id: conversationListingId,
        breeder_id: actualBreederId,
        listing_title: listingTitle,
        listing_url: listingUrl,
      });
      
      // Navigate to appropriate dashboard with conversation ID to auto-open it
      // Note: The conversation will be auto-deleted if no message is sent
      const dashboardPath = profile?.user_type === 'breeder' ? '/breeder-dashboard' : '/buyer-dashboard';
      navigate(`${dashboardPath}?section=messages&conversation=${conversation.id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  // Don't show button if user is the same as the profile owner
  if (user?.id === breederId) {
    return null;
  }

  return (
    <Button
      onClick={handleClick}
      disabled={createConversationMutation.isPending}
      className={className}
    >
      {createConversationMutation.isPending ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
      ) : (
        <MessageSquare className="w-4 h-4 mr-2" />
      )}
      Send Message
    </Button>
  );
};
